/**
 * Portfolio's "Where we own" map (V4 pfMapDraw, L30304): US states (Albers USA) with a pin per
 * facility that has catalog coordinates. Holdings are filled and coloured by how they stand
 * against plan, with a soft pulse and their descriptor letter; pipeline deals are dashed rings.
 * d3-geo, topojson-client and the us-atlas states are bundled (the design loads them from a
 * CDN); the states file is its own chunk, loaded when the map first draws.
 */
import { useEffect, useRef } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { sx } from '../../../ui/designStyles';

export interface PfPin {
  lat: number;
  lng: number;
  own: boolean;
  /** The descriptor letter drawn beside a holding ('' for pipeline). */
  label: string;
  title: string;
  col: string;
  r: number;
}

type Topo = { objects: { states: unknown } };
let topoPromise: Promise<Topo> | null = null;
const loadTopo = () => (topoPromise ??= import('us-atlas/states-10m.json').then((m) => ((m as { default?: Topo }).default ?? m) as Topo));

function draw(el: HTMLDivElement, topo: Topo, pins: PfPin[]) {
  const W = Math.max(240, el.clientWidth || 360);
  const H = Math.max(120, el.clientHeight || 150);
  const key = W + 'x' + H + JSON.stringify(pins.map((p) => [p.col, p.lat, p.lng, p.label]));
  const box = el as HTMLDivElement & { __k?: string };
  if (box.__k === key) return;
  box.__k = key;
  const states = feature(topo as never, (topo.objects as { states: never }).states) as unknown as FeatureCollection<Geometry>;
  const fit: FeatureCollection<Geometry> = { type: 'FeatureCollection', features: states.features.filter((f: Feature) => ['02', '15', '72'].indexOf(String(f.id)) < 0) };
  const proj = geoAlbersUsa().fitExtent([[4, 4], [W - 4, H - 4]], fit);
  const path = geoPath(proj);
  el.innerHTML = '';
  const svg = select(el).append('svg').attr('width', W).attr('height', H).style('display', 'block');
  svg
    .append('g')
    .selectAll('path')
    .data(states.features)
    .enter()
    .append('path')
    .attr('d', (f) => path(f) ?? '')
    .attr('fill', '#EFE9DC')
    .attr('stroke', 'rgba(34,39,46,.22)')
    .attr('stroke-width', 0.6);
  const g = svg.append('g');
  pins.forEach((p) => {
    const xy = proj([p.lng, p.lat]);
    if (!xy) return;
    const n = g.append('g').attr('transform', 'translate(' + xy[0] + ',' + xy[1] + ')');
    if (p.own)
      n.append('circle')
        .attr('r', p.r + 5)
        .attr('fill', p.col)
        .attr('opacity', 0.18)
        .append('animate')
        .attr('attributeName', 'r')
        .attr('values', p.r + 2 + ';' + (p.r + 8) + ';' + (p.r + 2))
        .attr('dur', '2.6s')
        .attr('repeatCount', 'indefinite');
    n.append('circle')
      .attr('r', p.r)
      .attr('fill', p.own ? p.col : '#FCFAF4')
      .attr('stroke', p.col)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', p.own ? null : '2 2');
    n.append('title').text(p.title);
    if (p.own && p.label)
      n.append('text').attr('x', p.r + 4).attr('y', 4).attr('font-size', 11).attr('font-weight', 700).attr('fill', '#22272E').attr('font-family', 'Archivo, sans-serif').text(p.label);
  });
}

export function PortfolioMap({ pins }: { pins: PfPin[] }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const topoRef = useRef<Topo | null>(null);
  const sig = JSON.stringify(pins);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let live = true;
    const redraw = () => {
      if (topoRef.current && ref.current) draw(ref.current, topoRef.current, pinsRef.current);
    };
    loadTopo().then(
      (t) => {
        if (!live) return;
        topoRef.current = t;
        redraw();
      },
      () => {
        if (live && ref.current) ref.current.innerHTML = '<div style="padding:10px;font-size:14px;color:#54606C;">Map could not load.</div>';
      },
    );
    if (typeof ResizeObserver === 'undefined') return () => void (live = false);
    const ro = new ResizeObserver(redraw);
    ro.observe(el);
    return () => {
      live = false;
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (topoRef.current && ref.current) draw(ref.current, topoRef.current, pinsRef.current);
  }, [sig]);

  return <div ref={ref} data-testid="pf-map" aria-label="Where we own" style={sx('flex:1;min-height:120px;position:relative;')} />;
}
